import Image from 'next/image';
import classNames from 'classnames';
import { Tooltip, svgSprite } from '../../../../hybrid/wrappers';
import css from './UserInfo.module.scss';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import UserContext, { UserProfileContext } from '../../../../common/utils/getContext';
import FormInput from '../../../../widgets/form/components/form-input/FormInput/FormInput';
import T from '../../../../common/components/T';
import { SITE_URL, API_SERVER_URL } from '../../../../common/utils/utils';
import Skeleton from '../../../../widgets/plug/Skeleton/Skeleton';
import { userInfoSkeleton } from './helpers';
import SponsorModal from '../SponsorInfo/components/Modal/SponsorModal';
import FormCheckboxSwitcher from '../../../../widgets/form/components/form-checkbox/FormCheckboxSwitcher/FormCheckboxSwitcher';
import AutoUpgrade from '../../../../widgets/autoUpgrade/AutoUpgrade';
import { dataSender, getData } from '../../../../common/utils/router';

const RenderData = ({ profileData, verification }) => {
    const { roles } = useContext(UserProfileContext);
    const { userToken, lang, setAlertData, display_country } = useContext(UserContext);
    const [autoUpgradeSettings, setAutoUpgradeSettings] = useState(null);
    const [autoUpgradeWidget, showAutoUpgradeWidget] = useState(false);
    const [showSponsor, setShowSponsor] = useState(false);
    const [autoUpgrade, showAutoUpgrade] = useState(false);
    const [autoUpgradeAvailable, setAutoUpgradeAvailable] = useState(null);
    const [isUpgraded, setIsUpgraded] = useState(false);
    const [reloadData, setReloadData] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);

    const registerDate = useMemo(() => {
        const date = profileData?.created_at_formatted;
        return date?.split(' ')[0];
    }, [profileData]);
    const isAdmin = useMemo(() => roles?.some((role) => ['superadmin'].includes(role.name)), [roles]);

    useEffect(() => {
        setBtnDisabled(true);
        getData('/api/v1/user/autoupgrade/available', userToken, lang).then((response) => {
            if (response?.data && response?.data?.length) {
                showAutoUpgradeWidget(true);
                setAutoUpgradeAvailable(response?.data);
                getData('/api/v1/user/autoupgrade', userToken, lang).then((response) => {
                    if (response?.data) {
                        setAutoUpgradeSettings(response?.data);
                        setIsUpgraded(true);
                    } else {
                        setAutoUpgradeSettings(null);
                        setIsUpgraded(false);
                    }
                    setBtnDisabled(false);
                });
            }
        });
    }, [userToken, lang, reloadData]);

    const removeAutoUpgrade = useCallback(() => {
        setBtnDisabled(true);
        dataSender(
            `${API_SERVER_URL}/api/v1/user/autoupgrade`,
            'DELETE',
            null,
            () => {
                setIsUpgraded(false);
                setReloadData((s) => !s);
                setBtnDisabled(false);
            },
            (error) => {
                setAlertData({
                    type: 'error',
                    cont: error.message || <T textName='error_occured' defaultStr='Произошла ошибка' />
                });
                setBtnDisabled(false);
            },
            userToken,
            lang
        );
    }, [userToken, lang]);

    return (
        <>
            <div className={css.block}>
                <div className={css.blockHead}>
                    <div className={css.blockHeadLeft}>
                        <div className={css.blockHeadAvatar}>
                            <div className={css.blockHeadAvatarStroke}>
                                {svgSprite('user-image-wrap', { className: css.blockHeadAvatarStrokeSvg })}
                            </div>
                            <div className={css.avatarMarketingInfo}>
                                <Tooltip
                                    text={
                                        <div className={css.rankTooltip}>
                                            <p className={css.rankTooltipLabel}>
                                                <T textName='qualification' defaultStr='Квалификация' page='/office' />
                                            </p>
                                            <p className={css.rankTooltipValue}>
                                                {profileData?.marketing_profile?.rank?.name || '-'}
                                            </p>
                                        </div>
                                    }
                                >
                                    <div className={css.package}>
                                        <Image
                                            className={classNames(`rank${profileData?.marketing_profile?.rank?.id || 0}`)}
                                            src={`/src/img/ranks/rank${profileData?.marketing_profile?.rank?.id || 0}.png`}
                                            alt={`rank${profileData?.marketing_profile?.rank?.id || 0}`}
                                            fill
                                        />
                                    </div>
                                </Tooltip>
                                <Tooltip
                                    text={
                                        <div className={css.rankTooltip}>
                                            <p className={css.rankTooltipLabel}>
                                                <T textName='package' defaultStr='Пакет' page='/office' />
                                            </p>
                                            <p className={css.rankTooltipValue}>
                                                {profileData?.marketing_profile?.package?.name || '-'}
                                            </p>
                                        </div>
                                    }
                                >
                                    <div className={css.package}>
                                        <Image
                                            className={classNames(
                                                `packageIcon${profileData?.marketing_profile?.package?.id || 0}`
                                            )}
                                            src={`/src/img/packages/package${
                                                profileData?.marketing_profile?.package?.id || 0
                                            }.png`}
                                            alt={`package${profileData?.marketing_profile?.package?.id || 0}`}
                                            fill
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                            {profileData?.image?.link || profileData?.image?.links?.preview ? (
                                <Image
                                    src={profileData?.image?.link || profileData?.image?.links?.preview}
                                    className={css.blockHeadAvatarUserImage}
                                    width={140}
                                    height={140}
                                    alt={'Avatar'}
                                    quality={100}
                                />
                            ) : (
                                <div className={css.blockHeadAvatarUserImage}>
                                    {svgSprite('plug-user-image', { className: css.blockHeadAvatarNoSvg })}
                                </div>
                            )}
                        </div>
                        <div className={css.blockHeadInfo}>
                            <div className={css.blockHeadInfoUsername}>
                                <p>{profileData?.username}</p>
                                {!profileData?.full_name ? (
                                    <Tooltip
                                        compStyle={{ tooltipWrapCustom: { position: 'absolute', top: 0, right: 0 } }}
                                        text={
                                            <div className={css.userActivityTooltip}>
                                                <p className={css.userActivityTooltipLabel}>
                                                    <T textName='activity-status' defaultStr='Статус активности' page='/office' />
                                                </p>
                                                <p
                                                    className={classNames(
                                                        css.userActivityTooltipValue,
                                                        {
                                                            [css.userActivityTooltipValueOk]:
                                                                profileData?.marketing_profile?.activity
                                                        },
                                                        {
                                                            [css.userActivityTooltipValueNot]:
                                                                !profileData?.marketing_profile?.activity
                                                        }
                                                    )}
                                                >
                                                    {profileData?.marketing_profile?.activity ? (
                                                        <T textName='activity-done' defaultStr='Выполнена' page='/office' />
                                                    ) : (
                                                        <T textName='activity-notdone' defaultStr='Не выполнена' page='/office' />
                                                    )}
                                                </p>
                                            </div>
                                        }
                                    >
                                        <div
                                            className={classNames(
                                                css.userActivity,
                                                { [css.userActivityOk]: profileData?.marketing_profile?.activity },
                                                { [css.userActivityNo]: !profileData?.marketing_profile?.activity }
                                            )}
                                        ></div>
                                    </Tooltip>
                                ) : null}
                                {verification?.data?.status?.value === 'verified' ? (
                                    <Tooltip
                                        text={
                                            <T textName='verification-passed' defaultStr='Верификация пройдена' page='/office' />
                                        }
                                    >
                                        <div className={css.verifiedDiv}>
                                            {svgSprite('verified-icon', { className: css.verifiedSvg })}
                                        </div>
                                    </Tooltip>
                                ) : null}
                            </div>
                            <div className={css.userFullname}>
                                <p>{profileData?.full_name}</p>
                                {profileData?.full_name ? (
                                    <Tooltip
                                        compStyle={{ tooltipWrapCustom: { position: 'absolute', top: 0, right: 0 } }}
                                        text={
                                            <div className={css.userActivityTooltip}>
                                                <p className={css.userActivityTooltipLabel}>
                                                    <T textName='activity-status' defaultStr='Статус активности' page='/office' />
                                                </p>
                                                <p
                                                    className={classNames(
                                                        css.userActivityTooltipValue,
                                                        {
                                                            [css.userActivityTooltipValueOk]:
                                                                profileData?.marketing_profile?.activity
                                                        },
                                                        {
                                                            [css.userActivityTooltipValueNot]:
                                                                !profileData?.marketing_profile?.activity
                                                        }
                                                    )}
                                                >
                                                    {profileData?.marketing_profile?.activity ? (
                                                        <T textName='activity-done' defaultStr='Выполнена' page='/office' />
                                                    ) : (
                                                        <T textName='activity-notdone' defaultStr='Не выполнена' page='/office' />
                                                    )}
                                                </p>
                                            </div>
                                        }
                                    >
                                        <div
                                            className={classNames(
                                                css.userActivity,
                                                { [css.userActivityOk]: profileData?.marketing_profile?.activity },
                                                { [css.userActivityNo]: !profileData?.marketing_profile?.activity }
                                            )}
                                        ></div>
                                    </Tooltip>
                                ) : null}
                            </div>
                            <div className={css.userRefLink}>
                                <FormInput
                                    attribute='copyRegister'
                                    readonly={true}
                                    disabled={true}
                                    copy={{
                                        hint: (
                                            <T
                                                textName='FormCopy-CopyReferalLink'
                                                defaultStr='Скопируйте реферальную ссылку сайта'
                                                page='/office'
                                            />
                                        ),
                                        positionDropdown: 'left',
                                        qr: (
                                            <T
                                                textName='inputData-copyRegister'
                                                defaultStr='На страницу регистрации'
                                                page='/office'
                                            />
                                        )
                                    }}
                                    compStyle={{
                                        input: {
                                            className: css.userRefLinkLabel
                                        },
                                        inner: {
                                            className: css.inputRefInner
                                        },
                                        wrapper: {
                                            className: css.inputRefWrapper
                                        }
                                    }}
                                    value={`${SITE_URL}/auth/register/ref/${profileData?.username}`}
                                    buttons={!!Number(display_country) ? {} : { binar_settings: true }}
                                />
                            </div>
                        </div>
                    </div>
                    {autoUpgradeWidget ? (
                        <div className={css.blockHeadRight}>
                            <FormCheckboxSwitcher
                                pending={btnDisabled}
                                title={{
                                    body: <T textName='auto-upgrade-package' defaultStr='Авто-апгрейд пакета' page='/office' />
                                }}
                                compStyle={{ label: css.autoUpgrade, title: css.autoUpgradeTitle }}
                                setFormData={() => {
                                    if (isUpgraded) {
                                        removeAutoUpgrade();
                                    } else {
                                        showAutoUpgrade(true);
                                        setIsUpgraded(true);
                                        setTimeout(() => {
                                            setIsUpgraded(false);
                                        });
                                    }
                                }}
                                value={isUpgraded}
                            />
                        </div>
                    ) : null}
                </div>
                <div className={css.blockBot}>
                    <div className={css.blockBotItem}>
                        <p className={css.blockBotItemTitle}>
                            <T textName='spiloverskaya_branch' defaultStr='Спиловерская ветка' page='/office' />
                        </p>
                        {isAdmin ? (
                            '-'
                        ) : (
                            <div className={css.blockBotItemBot}>
                                <div className={css.blockBotItemBotIcon}>
                                    {svgSprite(
                                        profileData?.marketing_profile?.position === 1
                                            ? 'structure-left-new'
                                            : 'structure-right-new',
                                        {
                                            className: css.blockBotItemBotSvg
                                        }
                                    )}
                                </div>
                                <p className={css.blockBotItemBotValue}>
                                    {profileData?.marketing_profile?.position === 1 ? (
                                        <T textName='left_feminine' defaultStr='Левая' page='/office' />
                                    ) : (
                                        <T textName='right_feminine' defaultStr='Правая' page='/office' />
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className={css.blockBotItem}>
                        <p className={css.blockBotItemTitle}>
                            <T textName='total_in_the_team' defaultStr='Всего в команде' page='/office' />
                        </p>
                        <div className={css.blockBotItemBot}>
                            <div className={css.blockBotItemBotIcon}>
                                {svgSprite('partners-new', { className: css.blockBotItemBotSvg })}
                            </div>
                            <p className={css.blockBotItemBotValue}>
                                {Number(
                                    profileData?.descendants_count || profileData?.marketing_profile?.descendants_count || '0'
                                ).toLocaleString()}
                            </p>
                        </div>
                    </div>
                    <div className={css.blockBotItem}>
                        <p className={css.blockBotItemTitle}>
                            <T textName='billing_week' defaultStr='Расчетная неделя' page='/office' />
                        </p>
                        <div className={css.blockBotItemBot}>
                            <div className={css.blockBotItemBotIcon}>
                                {svgSprite('clock-new', { className: css.blockBotItemBotSvg })}
                            </div>
                            <p className={css.blockBotItemBotValue}>
                                {profileData?.marketing_profile?.marketing_period_current?.id_formatted || '0'}
                            </p>
                        </div>
                    </div>
                    <div className={css.blockBotItem}>
                        <p className={css.blockBotItemTitle}>
                            <T textName='register_date' defaultStr='Дата регистрации' page='/office' />
                        </p>
                        <div className={css.blockBotItemBot}>
                            <div className={css.blockBotItemBotIcon}>
                                {svgSprite('calendar-new', { className: css.blockBotItemBotSvg })}
                            </div>
                            <p className={css.blockBotItemBotValue}>{registerDate || '-'}</p>
                        </div>
                    </div>
                </div>
                {profileData?.sponsor ? (
                    <button onClick={() => setShowSponsor(true)} className={classNames('defaultButton grow', css.mySponsorBtn)}>
                        <T textName='my-sponsor' defaultStr='Мой спонсор' page='/office' />
                    </button>
                ) : null}
            </div>
            {showSponsor ? <SponsorModal profileData={profileData} closeHandler={() => setShowSponsor(false)} /> : null}
            {autoUpgrade ? (
                <AutoUpgrade
                    available={autoUpgradeAvailable}
                    initialData={autoUpgradeSettings}
                    closeHandler={() => {
                        setReloadData((s) => !s);
                        showAutoUpgrade(false);
                    }}
                />
            ) : null}
        </>
    );
};

export default function UserInfo(props) {
    const { verification } = props;
    const { profileData, loaded } = useContext(UserProfileContext);
    return (
        <Skeleton
            wait={!!loaded && !!profileData}
            Component={RenderData}
            componentProps={{
                profileData,
                verification
            }}
            pattern={userInfoSkeleton}
            wrapperStyles={css.skeletonWrapperStyles}
        />
    );
}
